import { isWeekend } from 'date-fns';
import { isHoliday } from '@/lib/scheduling/holidays';
import type { DayShiftValidationResult, ShiftDataWithEmployee } from '@/lib/scheduling/unified-shift-validation-service';
import type { WeatherDisplayData } from '@/lib/weather/weather-service';

/**
 * Enriched day statistics that combines shift validation with additional context
 */
export interface EnrichedDayStats extends DayShiftValidationResult {
  // Weekend detection
  isWeekend: boolean;

  // Holiday information
  isHoliday: boolean;
  holidayName?: string;
  isNonWorkingDay?: boolean;

  // Weather (from real API data with temperature details)
  weather?: WeatherDisplayData;

  // Shift status breakdown
  shiftStatusBreakdown?: {
    late: number;
    missed: number;
    completed: number;
    completedLate: number;
    onTime: number;
    unassigned: number;
    inProgress: number;
    pending: number;
  };

  // Performance indicators
  hasIssues: boolean;
  allStaffOnTime: boolean;
}

// Mock weather generation removed - now using real weather data from weather-service.ts

/**
 * Calculate shift status breakdown from shift data
 * Uses pre-calculated status from shifts_history table (single source of truth)
 * This is the SAME status used in employee dashboard time tracking
 */
function calculateShiftStatusBreakdown(shifts: ShiftDataWithEmployee[]) {
  const breakdown = {
    late: 0,
    missed: 0,
    completed: 0,
    completedLate: 0,
    onTime: 0,
    unassigned: 0,
    inProgress: 0,
    pending: 0
  };

  shifts.forEach(shift => {
    // Count unassigned shifts (no employee assigned)
    if (!shift.employee_id) {
      breakdown.unassigned++;
      return;
    }

    // Use the pre-calculated status from shifts_history table
    // Status values come from ShiftStatus enum in shift-status-types.ts
    // and are calculated by shift-status-service.ts (same as employee dashboard)
    const status = shift.status?.toLowerCase() || 'pending';

    // Map database status values to breakdown counters
    switch (status) {
      case 'late':
      case 'arrived_late':
        breakdown.late++;
        break;
      case 'missed':
      case 'no_show':
        breakdown.missed++;
        break;
      case 'completed':
        breakdown.completed++;
        break;
      case 'completed_late':
      case 'left_early':
        breakdown.completedLate++;
        break;
      case 'on_time':
        breakdown.onTime++;
        break;
      case 'in_progress':
        breakdown.inProgress++;
        break;
      case 'pending':
      case 'unassigned':
        breakdown.pending++;
        break;
      default:
        // Unknown status - count as pending
        breakdown.pending++;
        break;
    }
  });

  return breakdown;
}

/**
 * Enrich day validation result with holiday, weekend, and shift status information
 *
 * @param validation - The base validation result from unified-shift-validation-service
 * @param countryCode - Country code for holiday detection (e.g., 'US', 'SK', 'DE')
 * @param weather - Optional weather display data with temperature details from weather-service.ts
 * @returns Enriched day statistics with additional context
 */
export function enrichDayValidation(
  validation: DayShiftValidationResult,
  countryCode: string,
  weather?: WeatherDisplayData
): EnrichedDayStats {
  const { date, shifts } = validation;

  // Detect weekend
  const weekend = isWeekend(date);

  // Detect holiday
  const holiday = isHoliday(date, countryCode);

  // Calculate shift status breakdown if shifts are available
  const statusBreakdown = shifts && shifts.length > 0
    ? calculateShiftStatusBreakdown(shifts)
    : undefined;

  // Determine if there are any issues
  const hasIssues = Boolean(
    statusBreakdown && (
      statusBreakdown.late > 0 ||
      statusBreakdown.missed > 0 ||
      statusBreakdown.completedLate > 0
    )
  );

  // Determine if all staff were on time
  // Requirements:
  // 1. Must have shifts
  // 2. All shifts must be assigned (no unassigned shifts)
  // 3. No late arrivals, missed shifts, or late completions
  // 4. At least some shifts have positive attendance status (completed/onTime)
  const allStaffOnTime = Boolean(
    shifts &&
    shifts.length > 0 &&
    statusBreakdown &&
    statusBreakdown.unassigned === 0 && // All shifts must be assigned
    statusBreakdown.late === 0 &&
    statusBreakdown.missed === 0 &&
    statusBreakdown.completedLate === 0 &&
    // At least some shifts have good attendance status
    (statusBreakdown.completed > 0 || statusBreakdown.onTime > 0)
  );

  return {
    ...validation,
    isWeekend: weekend,
    isHoliday: Boolean(holiday),
    holidayName: holiday?.name,
    isNonWorkingDay: holiday?.isNonWorkingDay,
    weather,
    shiftStatusBreakdown: statusBreakdown,
    hasIssues,
    allStaffOnTime
  };
}

/**
 * Batch enrich multiple day validations
 *
 * @param validations - Map of day validations keyed by dateKey
 * @param countryCode - Country code for holiday detection
 * @param weatherMap - Optional map of weather display data keyed by dateKey (YYYY-MM-DD)
 * @returns Map of enriched day statistics
 */
export function enrichDayValidations(
  validations: Map<string, DayShiftValidationResult>,
  countryCode: string,
  weatherMap?: Map<string, WeatherDisplayData>
): Map<string, EnrichedDayStats> {
  const enriched = new Map<string, EnrichedDayStats>();

  validations.forEach((validation, dateKey) => {
    const weather = weatherMap?.get(dateKey);
    enriched.set(dateKey, enrichDayValidation(validation, countryCode, weather));
  });

  return enriched;
}
